/**
 * ProductSetChatCard — the product set card in Upright web chat (PLT-7433).
 *
 * `get_dd_product_set`, `update_dd_product_set` and `save_dd_product_set_from_files` tag their result
 * for the `product-set-card` MCP app. Claude and ChatGPT render that app; Upright web chat renders this
 * card from the same result instead, so the product editor opens inside the card rather than beside
 * the chat, as the site review does. `useProductSetCardPayload` keeps the result current: where the
 * card stands among the later cards in its thread, and a re-read of the set after each save.
 *
 * The card has a read mode and an edit mode. "Edit products" opens the product editor as the Edit
 * company dialog runs it: the same table, add and edit dialogs, matching and attribute views, and an
 * explicit edit that only Save commits and Cancel discards. Save announces itself
 * (`notifyProductSetSaved`), the card re-reads the set, and the card returns to the list as saved.
 *
 * Approving sends the chat the message the agent answers with `publish_dd_product_set` or, when the
 * company's sites were identified, with the site review that comes before its results (PLT-6999). The
 * card never publishes itself, so approval keeps one path, with the agent's checks on it (PLT-7087).
 */
import { useCallback, useRef, useState } from 'react';
import { useAssistantRuntime, useThread } from '@assistant-ui/react';
import { Banner } from '@primer/react/experimental';
import { type PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { countPiuLeafProducts } from '@upright/common/model/input/companies/products/product-input-ui/utils';
import { Button } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Heading, HeadingGroup, Paragraph, Text } from '@upright/ui/common/generic/components/typography';
import { ProductInputUiMain } from '@upright/ui/views/dma-v2-view/shared/product-input-ui';
import { useProductInputDataState } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/main-state';
import { ProductControlButtons } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/product-control-buttons';
import { ProductEditingContext } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/types';
import { ProductEditDialog } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/product-edit-dialog';
import { CustomCompanyAttributeAccess } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/product-attribute-access';
import { ProductFooter } from '@upright/ui/views/dma-v2-view/shared/product-input-ui/product-footer';
import { NoProductsEmptyState } from '@upright/ui/views/dma-v2-view/onboarding-wizard/select-products/no-products-empty-state';
import { type ProductSetCardStanding } from './product-set-card-standing';
import { notifyProductSetSaved } from './product-set-saved-events';
import { FOLLOW_THREAD_EVENT } from './upright-chat-ui-common';
import { useProductSetCardPayload } from './use-product-set-card-payload';
import styles from './product-set-chat-card.module.css';

/** One product as `get_dd_product_set` returns it. */
interface ProductEntry {
  productId: string;
  productName: string;
  description?: string;
  /** 0..1 ratio of the set's revenue; absent when the set is unweighted. */
  revenueShare?: number;
}

/** The fields of the tool result the card reads; the MCP app reads the same ones. */
interface ProductSetCardData {
  offerFinancialStatements?: boolean;
  companyId: string;
  status: string;
  companyName?: string;
  products?: ProductEntry[];
  publishState?: 'draft' | 'publishing' | 'published';
  /** Present before the first approval when the company's sites were identified alongside its products. */
  siteIdentification?: { status: string };
  /** Present when the company is a buyer's supplier rather than an investor's custom company (PLT-7204). */
  supplier?: true;
  /** Set by `useProductSetCardPayload` when a later card replaced this one or the set was approved. */
  cardState?: Exclude<ProductSetCardStanding, 'current'>;
  errorMessage?: string;
}

function asProductSetCardData(value: unknown): ProductSetCardData | null {
  if (typeof value !== 'object' || value === null) return null;
  const { companyId, status } = value as { companyId?: unknown; status?: unknown };
  return typeof companyId === 'string' && typeof status === 'string' ? (value as ProductSetCardData) : null;
}

function sortByShare(products: ProductEntry[]): ProductEntry[] {
  return [...products].sort((a, b) => (b.revenueShare ?? -1) - (a.revenueShare ?? -1));
}

/** One decimal, as the product editor shows a share; a share too small for that still reads as present. */
function formatRevenueShare(share: number): string {
  if (share > 0 && share < 0.0005) return '<0.1%';
  return `${(share * 100).toFixed(1)}%`;
}

function productCountLabel(count: number): string {
  return count === 1 ? '1 product' : `${count} products`;
}

export function ProductSetChatCard({ toolCallId, toolResult }: { toolCallId: string; toolResult: unknown }) {
  const data = asProductSetCardData(useProductSetCardPayload({ toolCallId, toolResult }));
  if (!data) return null;
  if (data.cardState === 'replaced') {
    return (
      <DataCard>
        <Text size="sm" color="gray-600">
          Replaced by the updated version below.
        </Text>
      </DataCard>
    );
  }
  if (data.status === 'error') {
    return (
      <DataCard>
        <Stack gap="md">
          <CardHeader title="Product set unavailable" items={[data.companyName]} />
          <Text size="sm" color="gray-600">
            {data.errorMessage ?? 'Something went wrong while preparing the product set.'}
          </Text>
        </Stack>
      </DataCard>
    );
  }
  // A set still generating draws no card: the tool answers it as no data.
  if (data.status !== 'ready') return null;
  const products = sortByShare(data.products ?? []);
  if (data.cardState === 'approved' || data.publishState === 'publishing' || data.publishState === 'published') {
    return (
      <DataCard>
        <Stack gap="lg">
          <CardHeader title="Product set approved" items={[data.companyName, productCountLabel(products.length)]} />
          <ProductList products={products} offerEditor={false} />
          <Text size="sm" color="gray-600">
            Results are calculated from this set.
          </Text>
        </Stack>
      </DataCard>
    );
  }
  return <ReadyCard data={data} products={products} />;
}

/** Subject first, then the count, joined the way the MCP cards' `CardSubtitle` joins them. */
function CardHeader({ title, items }: { title: string; items: Array<string | undefined> }) {
  const subtitle = items.filter((item): item is string => item !== undefined && item !== '').join(' · ');
  return (
    <HeadingGroup>
      <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
        {title}
      </Heading>
      {subtitle !== '' && (
        <Paragraph size="sm" height="xs" color="gray-600">
          {subtitle}
        </Paragraph>
      )}
    </HeadingGroup>
  );
}

/** The top five products, by revenue share, and a count of the rest. */
function ProductList({ products, offerEditor }: { products: ProductEntry[]; offerEditor: boolean }) {
  const top = products.slice(0, 5);
  const extra = products.length - top.length;
  // Shares show only when the set is weighted; a row without one then reads as no data, not 0%.
  const showShares = products.some(p => p.revenueShare != null);
  return (
    <div>
      {showShares && (
        <div className={styles.listLabel}>
          <Text size="sm" color="gray-600">
            Share of revenue
          </Text>
        </div>
      )}
      {/* Roles rather than ul/li: the chat styles every list in an answer as markdown, indents included. */}
      <div role="list" className={styles.list}>
        {top.map(p => (
          <div role="listitem" key={p.productId} className={styles.row}>
            <span className={styles.rowLine}>
              <Text color="gray-900">{p.productName}</Text>
              {showShares && (
                <span className={styles.share}>
                  <Text color="gray-900">{p.revenueShare != null ? formatRevenueShare(p.revenueShare) : '—'}</Text>
                </span>
              )}
            </span>
            {p.description && (
              <Text size="sm" color="gray-600">
                {p.description}
              </Text>
            )}
          </div>
        ))}
      </div>
      {extra > 0 && (
        <Text size="sm" color="gray-600">
          +{extra} more.{offerEditor && ' Use “Edit products” to see and adjust the full list.'}
        </Text>
      )}
    </div>
  );
}

function ReadyCard({ data, products }: { data: ProductSetCardData; products: ProductEntry[] }) {
  const runtime = useAssistantRuntime();
  const isThreadRunning = useThread(state => state.isRunning);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Locks the card once the approval is sent, until the card that follows it marks this one approved;
  // a second press would only send the message again.
  const [approvalSent, setApprovalSent] = useState(false);
  // PLT-6999: with sites identified, they are reviewed before anything is computed.
  const approval =
    data.siteIdentification !== undefined
      ? { label: 'Looks good, review sites', message: 'Looks good, review sites.' }
      : data.offerFinancialStatements
        ? {
            label: 'Looks good, add financials',
            message: `Looks good, add financials for ${data.companyName ?? 'this company'}.`,
          }
        : { label: 'Looks good, compute results', message: 'Looks good, compute results.' };

  const approve = () => {
    setApprovalSent(true);
    // The approval is the user's next turn: take them to it, as a message sent from an MCP card does.
    cardRef.current?.closest('.aui-thread-viewport')?.dispatchEvent(new Event(FOLLOW_THREAD_EVENT));
    runtime.thread.append(approval.message);
  };

  return (
    // `product-set-chat-card` keeps the chat's prose-table styles off the editor's table
    // (upright-chat-theme.css), as `site-locations-batch-card` does for the site table.
    <div ref={cardRef} className="product-set-chat-card">
      <DataCard>
        <Stack gap="lg">
          <CardHeader
            title={isEditing ? 'Edit products' : 'Product set ready'}
            items={[data.companyName, productCountLabel(products.length)]}
          />
          {isEditing ? (
            // Approval waits for the edit to end: an open edit is either saved or discarded first,
            // so the set approved is always the one saved.
            <ProductSetEditor
              companyId={data.companyId}
              supplier={data.supplier === true}
              onClose={() => {
                setIsEditing(false);
              }}
            />
          ) : (
            <>
              <ProductList products={products} offerEditor={true} />
              {/* Secondary action first, primary last. Approval waits for the chat's turn to end, as
                  the site review's does, so the message never lands inside a running answer. */}
              <Stack row gap="sm" align="center" wrap>
                <Button
                  variant="outlined"
                  size="small"
                  isDisabled={approvalSent}
                  onPress={() => {
                    setIsEditing(true);
                  }}
                >
                  Edit products
                </Button>
                <Button variant="contained" size="small" isDisabled={approvalSent || isThreadRunning} onPress={approve}>
                  {approval.label}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </DataCard>
    </div>
  );
}

/**
 * The product editor inside the card, run as the Edit company dialog runs it: an explicit edit that is
 * saved only by Save and discarded by Cancel (`explicitSave`), rather than the review step's autosave.
 * The editor opens already editing (`startInEditMode`), and Save or Cancel ends the edit and returns
 * to the list.
 */
function ProductSetEditor({
  companyId,
  supplier,
  onClose,
}: {
  companyId: string;
  supplier: boolean;
  onClose: () => void;
}) {
  // The hook wires `publishAndComputeResults` to a Continue button this editor does not render: the
  // card approves through the chat instead.
  const noopPublish = useCallback(() => {
    /* no-op */
  }, []);
  const {
    data,
    updateProduct,
    showMatching,
    setShowMatching,
    showAttributes,
    setShowAttributes,
    addProduct,
    clearAll,
    resetChanges,
    autoSaveStatus,
    duplicateProduct,
    deleteProduct,
    finishStatus,
    disabled,
    readOnly,
    isLoadingProductSet,
    isEditing,
    hasUnsavedEdits,
    startEditing,
    saveEdits,
    cancelEditing,
    saveStatus,
    saveError,
  } = useProductInputDataState({
    companyId,
    jobStatus: undefined,
    jobQueryStatus: 'idle',
    finishStatus: 'idle',
    publishAndComputeResults: noopPublish,
    mode: 'dashboard',
    explicitSave: true,
    // The card's "Edit products" already asked to edit, so the edit starts with the editor rather
    // than behind a second Edit button. Save and Cancel end it, and then the editor closes.
    startInEditMode: true,
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleSaveCreatedProduct = useCallback(
    (product: PiuProductState) => {
      addProduct(product.title, product.description, product.revenueShare);
      setShowCreateDialog(false);
    },
    [addProduct],
  );

  const totalProducts = data?.products.length ?? 0;

  // An investor's custom company follows the custom company rule; a supplier keeps the plan rule, as
  // on the Suppliers page.
  return (
    <CustomCompanyAttributeAccess enabled={!supplier}>
      <Stack gap="md">
        <ProductControlButtons
          showMatching={showMatching}
          onToggleMatching={() => {
            setShowMatching(!showMatching);
          }}
          showAttributes={showAttributes}
          onToggleAttributes={() => {
            setShowAttributes(!showAttributes);
          }}
          onAddProduct={() => {
            setShowCreateDialog(true);
          }}
          onClearAll={clearAll}
          onResetChanges={resetChanges}
          // The Edit company dialog's toolbar: add, Cancel, Save and the menu while editing.
          uiContext={ProductEditingContext.DASHBOARD}
          onUpdateClick={noopPublish}
          autoSaveStatus={autoSaveStatus}
          blanketStatus={data?.blanketStatus}
          finishStatus={finishStatus}
          disabled={disabled}
          readOnly={readOnly}
          companyId={companyId}
          isEditing={isEditing}
          hasUnsavedEdits={hasUnsavedEdits}
          onStartEditing={startEditing}
          onSaveEdits={() => {
            saveEdits(() => {
              // The card's list re-reads the saved set (PLT-7087).
              notifyProductSetSaved(companyId);
              onClose();
            });
          }}
          onCancelEditing={() => {
            cancelEditing();
            onClose();
          }}
          saveStatus={saveStatus}
        />
        {/* A failed Save leaves the editor open with the changes intact, so without this a failure
            looks like nothing happened (PLT-6599). */}
        {saveError !== undefined && (
          <Banner
            variant="critical"
            title="Saving the products failed"
            description={`Your changes have not been saved. Try again. (${saveError.message})`}
          />
        )}
        {showCreateDialog && (
          <ProductEditDialog
            onClose={() => {
              setShowCreateDialog(false);
            }}
            product={{
              id: '',
              title: '',
              description: '',
              revenueShare: undefined,
              productMatchResult: undefined,
            }}
            onSave={handleSaveCreatedProduct}
            mode="create"
            companyId={companyId}
          />
        )}
        {isLoadingProductSet ? (
          <Text size="sm" color="gray-600">
            Loading the product set…
          </Text>
        ) : totalProducts === 0 ? (
          <NoProductsEmptyState
            onAddProduct={() => {
              setShowCreateDialog(true);
            }}
          />
        ) : (
          // The product table is wider than a narrow chat column, so it scrolls sideways inside the card.
          <div className={styles.table}>
            <ProductInputUiMain
              companyData={data}
              showMatching={showMatching}
              showAttributes={showAttributes}
              onProductUpdate={updateProduct}
              duplicateProduct={duplicateProduct}
              deleteProduct={deleteProduct}
              disabled={disabled || !isEditing}
              readOnly={readOnly}
              companyId={companyId}
            />
          </div>
        )}
        {/* Nothing on the right: Save and Cancel sit in the toolbar, as in the Edit company dialog. */}
        <ProductFooter productCount={countPiuLeafProducts(data?.products)}>{null}</ProductFooter>
      </Stack>
    </CustomCompanyAttributeAccess>
  );
}
